import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Command } from 'cmdk';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { leadsAPI, candidatesAPI, jobsAPI, aiAPI, formatApiError } from '../../services/api';
import { prefetchRoute } from '../../routes/lazyPages';
import AnimatedIcon from './AnimatedIcon';

/**
 * Ctrl+K / ⌘K command palette.
 *  - Ask Nexus: plain language → a filtered list or a page (backend/ai_routes.py).
 *  - Live record search across leads, candidates and jobs.
 *  - Every page and common action, filtered by the user's role.
 *  - Recently visited pages.
 */

const PAGES = [
  { path: '/sales',                  icon: 'dashboard',        label: 'Sales home',          module: 'sales' },
  { path: '/sales/leads',            icon: 'domain',           label: 'Leads',               module: 'sales', kw: 'companies clients prospects accounts table' },
  { path: '/sales/pipeline',         icon: 'view_kanban',      label: 'Leads board (pipeline)', module: 'sales', kw: 'kanban stages deals board' },
  { path: '/sales/outreach?tab=sequences', icon: 'schedule_send', label: 'Outreach · Sequences', module: 'sales', kw: 'cadence email steps' },
  { path: '/sales/outreach?tab=bulk', icon: 'forward_to_inbox', label: 'Outreach · Bulk email', module: 'sales', kw: 'mass email campaign' },
  { path: '/sales/call-cadence',     icon: 'phone_in_talk',    label: 'Calls',               module: 'sales', kw: 'call boards dialer cadence spreadsheet' },
  { path: '/sales/hiring-intel',     icon: 'radar',            label: 'Hiring signals',      module: 'sales', kw: 'hiring intel jobs scraping companies scan' },
  { path: '/sales/reports?tab=tracker', icon: 'analytics',     label: 'Reports · Activity & targets', module: 'sales', kw: 'tracker coaching' },
  { path: '/sales/reports?tab=channels', icon: 'hub',          label: 'Reports · Channels',  module: 'sales', kw: 'apollo cloudtalk command centre calls emails' },
  { path: '/sales/reports?tab=log',  icon: 'edit_note',        label: 'Reports · Daily log', module: 'sales', kw: 'log today linkedin activity' },
  { path: '/sales/data?tab=import',  icon: 'upload_file',      label: 'Lead data · Import',  module: 'sales', perm: 'canViewImport', kw: 'csv excel upload' },
  { path: '/sales/data?tab=enrich',  icon: 'auto_fix_high',    label: 'Lead data · Enrich',  module: 'sales', perm: 'canViewImport', kw: 'apollo enrichment' },
  { path: '/sales/tasks',            icon: 'task_alt',         label: 'Sales tasks',         module: 'sales', kw: 'todo reminders' },
  { path: '/recruitment',            icon: 'space_dashboard',  label: 'Recruitment home',    module: 'recruitment' },
  { path: '/recruitment/jobs',       icon: 'work',             label: 'Jobs',                module: 'recruitment', kw: 'roles vacancies' },
  { path: '/recruitment/candidates', icon: 'person_search',    label: 'Candidates',          module: 'recruitment', kw: 'talent cvs people table' },
  { path: '/recruitment/pipeline',   icon: 'account_tree',     label: 'Candidates board (pipeline)', module: 'recruitment', kw: 'kanban stages' },
  { path: '/recruitment/resume-hub?tab=match', icon: 'auto_awesome', label: 'AI Match (ATS)', module: 'recruitment', kw: 'ats match rank shortlist jd job description' },
  { path: '/recruitment/resume-hub?tab=bulk', icon: 'folder_zip', label: 'Bulk CV upload',  module: 'recruitment', kw: 'cv zip upload parse' },
  { path: '/recruitment/interviews', icon: 'event',            label: 'Interviews',          module: 'recruitment', kw: 'schedule calendar' },
  { path: '/recruitment/tasks',      icon: 'checklist',        label: 'Recruitment tasks',   module: 'recruitment' },
  { path: '/timesheet',              icon: 'schedule',         label: 'My timesheet',        perm: 'viewOwnTimesheet', kw: 'hours' },
  { path: '/timesheet/approvals',    icon: 'fact_check',       label: 'Timesheet approvals', perm: 'viewTimesheetApprovals' },
  { path: '/ceo',                    icon: 'insights',         label: 'CEO dashboard',       perm: 'viewCEO', kw: 'overview' },
  { path: '/audit-log',              icon: 'history',          label: 'Audit log',           perm: 'viewCEO' },
  { path: '/expenses',               icon: 'receipt_long',     label: 'Expenses',            perm: 'viewExpenses' },
  { path: '/settings',               icon: 'settings',         label: 'Settings',            perm: 'viewSettings', kw: 'users roles integrations ai providers' },
];

const ACTIONS = [
  { id: 'log-day',    icon: 'edit_calendar', label: "Log today's outreach",     path: '/sales/reports?tab=log', module: 'sales' },
  { id: 'new-task',   icon: 'add_task',      label: 'Create a task',            path: '/sales/tasks',        module: 'sales' },
  { id: 'reminders',  icon: 'alarm',         label: 'See my reminders',         path: '/sales/tasks?tab=reminders', module: 'sales' },
  { id: 'scan',       icon: 'radar',         label: 'Run a hiring-signals scan', path: '/sales/hiring-intel', module: 'sales' },
  { id: 'import',     icon: 'upload_file',   label: 'Import a spreadsheet of leads', path: '/sales/data?tab=import', module: 'sales', perm: 'canViewImport' },
  { id: 'ai-match',   icon: 'auto_awesome',  label: 'Rank candidates for a job description (AI Match)', path: '/recruitment/resume-hub?tab=match', module: 'recruitment' },
  { id: 'upload-cvs', icon: 'cloud_upload',  label: 'Upload CVs in bulk',       path: '/recruitment/resume-hub?tab=bulk', module: 'recruitment' },
];

const RECENT_KEY = 'nexus_recent_pages';
const readRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; } };

export function rememberPage(path) {
  try {
    const hit = PAGES.find(p => p.path.split('?')[0] === path);
    if (!hit) return;
    const next = [path, ...readRecent().filter(p => p !== path)].slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch { /* storage blocked */ }
}

const pick = (res, key) => {
  const d = res && res.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d[key])) return d[key];
  if (d && Array.isArray(d.data)) return d.data;
  return [];
};

const ItemIcon = ({ name }) => (
  <span className="cmdk-item-icon"><span className="material-symbols-outlined">{name}</span></span>
);

export default function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, can, hasModule } = useAuth();
  const [query, setQuery] = useState('');
  const [records, setRecords] = useState({ leads: [], candidates: [], jobs: [] });
  const [searching, setSearching] = useState(false);
  const [ask, setAsk] = useState({ state: 'idle', result: null, error: '' });
  const reqId = useRef(0);

  const allowed = useCallback((item) => {
    if (!user) return false;
    if (item.module && !hasModule(item.module)) return false;
    if (item.perm && !can(item.perm)) return false;
    return true;
  }, [user, can, hasModule]);

  const pages = useMemo(() => PAGES.filter(allowed), [allowed]);
  const actions = useMemo(() => ACTIONS.filter(allowed), [allowed]);
  const recent = useMemo(() => (open ? readRecent() : [])
    .map(p => pages.find(x => x.path.split('?')[0] === p)).filter(Boolean)
    .filter(p => p.path !== location.pathname).slice(0, 4), [open, pages, location.pathname]);

  // reset when closed
  useEffect(() => {
    if (!open) { setQuery(''); setRecords({ leads: [], candidates: [], jobs: [] }); setAsk({ state: 'idle', result: null, error: '' }); }
  }, [open]);

  // live record search (debounced)
  useEffect(() => {
    const q = query.trim();
    if (!open || q.length < 2) { setRecords({ leads: [], candidates: [], jobs: [] }); setSearching(false); return undefined; }
    const id = ++reqId.current;
    setSearching(true);
    const t = setTimeout(async () => {
      const calls = [
        hasModule('sales') ? leadsAPI.getAll({ search: q, limit: 5 }).catch(() => null) : Promise.resolve(null),
        hasModule('recruitment') ? candidatesAPI.getAll({ search: q, limit: 5 }).catch(() => null) : Promise.resolve(null),
        hasModule('recruitment') ? jobsAPI.getAll({ search: q }).catch(() => null) : Promise.resolve(null),
      ];
      const [l, c, j] = await Promise.all(calls);
      if (id !== reqId.current) return;
      setRecords({ leads: pick(l, 'leads').slice(0, 5), candidates: pick(c, 'candidates').slice(0, 5), jobs: pick(j, 'jobs').slice(0, 4) });
      setSearching(false);
    }, 250);
    return () => clearTimeout(t);
  }, [query, open, hasModule]);

  const go = useCallback((path) => { onClose(); navigate(path); }, [navigate, onClose]);

  const runAsk = useCallback(async () => {
    const q = query.trim();
    if (q.length < 3 || ask.state === 'thinking') return;
    setAsk({ state: 'thinking', result: null, error: '' });
    try {
      const res = await aiAPI.ask(q);
      const r = res.data || {};
      setAsk({ state: 'done', result: r, error: '' });
      if (r.url) setTimeout(() => go(r.url), 700);
    } catch (e) {
      setAsk({ state: 'error', result: null, error: formatApiError(e) });
    }
  }, [query, ask.state, go]);

  if (!open) return null;
  const q = query.trim();
  const isRecruit = location.pathname.startsWith('/recruitment');

  return (
    <div className="cmdk-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cmdk-panel" role="dialog" aria-modal="true" aria-label="Command palette">
        <Command label="Command palette" loop
                 onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } }}>
          <div className="cmdk-input-wrap">
            <span className="material-symbols-outlined">search</span>
            <Command.Input autoFocus value={query} onValueChange={setQuery}
                           placeholder="Search records, jump to a page, or ask Nexus…" />
          </div>

          {ask.state !== 'idle' && (
            <div style={{ padding: '8px 12px 0' }}>
              <div className={`cmdk-ask${ask.state === 'thinking' ? ' is-thinking' : ''}`}>
                <div className="cmdk-ask-inner">
                  {ask.state === 'thinking' && <span>Nexus is working out where to take you…</span>}
                  {ask.state === 'done' && ask.result && (
                    <span><strong>{ask.result.url ? 'Opening: ' : 'Ask Nexus: '}</strong>{ask.result.explanation}
                      {ask.result.provider === 'keywords' ? ' ' : ''}</span>
                  )}
                  {ask.state === 'error' && <span><strong>Ask Nexus is unavailable: </strong>{ask.error}</span>}
                </div>
              </div>
            </div>
          )}

          <Command.List>
            {/* The Search group is always shown once something is typed, so "empty" can only
                mean a one-letter query with nothing matching. */}
            <Command.Empty>No matching pages.</Command.Empty>
            {searching && q.length >= 2 && <div className="cmdk-item-sub" style={{ padding: '6px 12px' }}>Searching records…</div>}

            {q.length >= 3 && (
              <Command.Group heading="Ask Nexus" forceMount>
                <Command.Item value={`ask-nexus ${q}`} keywords={[q]} forceMount onSelect={runAsk}>
                  <span className="cmdk-item-icon" style={{ background: 'rgba(68,104,176,0.12)' }}>
                    <AnimatedIcon name="auto_awesome" play="sparkle" loop={ask.state === 'thinking'} size="1.05rem" />
                  </span>
                  <span>“{q}”<span className="cmdk-item-sub">Find or open this with AI, e.g. “interested fintechs in Dublin”</span></span>
                  <span className="cmdk-item-hint">Enter</span>
                </Command.Item>
              </Command.Group>
            )}

            {q.length >= 2 && (records.leads.length > 0 || records.candidates.length > 0 || records.jobs.length > 0) && (
              <Command.Group heading="Records" forceMount>
                {records.leads.map(l => (
                  <Command.Item key={`l-${l.id}`} value={`lead ${l.id} ${l.company || ''} ${l.full_name || ''}`} keywords={[q]}
                                onSelect={() => go(`/sales/leads/${l.id}`)}>
                    <ItemIcon name="domain" />
                    <span>{l.company || l.full_name || 'Lead'}<span className="cmdk-item-sub">{[l.full_name, l.job_title, l.status].filter(Boolean).join(' · ')}</span></span>
                    <span className="cmdk-item-hint">Lead</span>
                  </Command.Item>
                ))}
                {records.candidates.map(c => (
                  <Command.Item key={`c-${c.id}`} value={`candidate ${c.id} ${c.full_name || ''}`} keywords={[q]}
                                onSelect={() => go(`/recruitment/candidates/${c.id}`)}>
                    <ItemIcon name="person" />
                    <span>{c.full_name || 'Candidate'}<span className="cmdk-item-sub">{[c.candidate_role, c.current_company, c.status].filter(Boolean).join(' · ')}</span></span>
                    <span className="cmdk-item-hint">Candidate</span>
                  </Command.Item>
                ))}
                {records.jobs.map(j => (
                  <Command.Item key={`j-${j.id}`} value={`job ${j.id} ${j.title || ''}`} keywords={[q]}
                                onSelect={() => go(`/recruitment/jobs/${j.id}`)}>
                    <ItemIcon name="work" />
                    <span>{j.title || 'Job'}<span className="cmdk-item-sub">{[j.client_name || j.department, j.location].filter(Boolean).join(' · ')}</span></span>
                    <span className="cmdk-item-hint">Job</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {q.length >= 1 && (
              <Command.Group heading="Search" forceMount>
                {hasModule('sales') && (
                  <Command.Item value={`search-leads ${q}`} keywords={[q]} forceMount
                                onSelect={() => go(`/sales/leads?q=${encodeURIComponent(q)}`)}>
                    <ItemIcon name="manage_search" />
                    <span>Search leads for “{q}”</span>
                    {!isRecruit && <span className="cmdk-item-hint">Same as Enter in the search bar</span>}
                  </Command.Item>
                )}
                {hasModule('recruitment') && (
                  <Command.Item value={`search-candidates ${q}`} keywords={[q]} forceMount
                                onSelect={() => go(`/recruitment/candidates?q=${encodeURIComponent(q)}`)}>
                    <ItemIcon name="person_search" />
                    <span>Search candidates for “{q}”</span>
                  </Command.Item>
                )}
              </Command.Group>
            )}

            {q.length === 0 && recent.length > 0 && (
              <Command.Group heading="Recent">
                {recent.map(p => (
                  <Command.Item key={`r-${p.path}`} value={`recent ${p.label}`} onSelect={() => go(p.path)}
                                onMouseEnter={() => prefetchRoute(p.path)}>
                    <ItemIcon name={p.icon} /><span>{p.label}</span><span className="cmdk-item-hint">{p.path}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            <Command.Group heading="Pages">
              {pages.map(p => (
                <Command.Item key={p.path} value={`page ${p.label} ${p.path}`} keywords={p.kw ? p.kw.split(' ') : []}
                              onSelect={() => go(p.path)} onMouseEnter={() => prefetchRoute(p.path)}>
                  <ItemIcon name={p.icon} /><span>{p.label}</span><span className="cmdk-item-hint">{p.path}</span>
                </Command.Item>
              ))}
            </Command.Group>

            {actions.length > 0 && (
              <Command.Group heading="Actions">
                {actions.map(a => (
                  <Command.Item key={a.id} value={`action ${a.label}`} onSelect={() => go(a.path)}>
                    <ItemIcon name={a.icon} /><span>{a.label}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}
          </Command.List>

          <div className="cmdk-footer">
            <span><span className="kbd">↑</span> <span className="kbd">↓</span> move</span>
            <span><span className="kbd">Enter</span> open</span>
            <span><span className="kbd">Esc</span> close</span>
            <span style={{ marginLeft: 'auto' }}><span className="kbd">Ctrl</span> <span className="kbd">K</span> anywhere</span>
          </div>
        </Command>
      </div>
    </div>
  );
}
